import React, { useState, useEffect, useMemo } from "react";
import { GrClose } from "react-icons/gr";
import { toast } from "react-toastify";
import Api from "../../Api/api";
import { useStateValue } from "../../states/StateProvider";
import { ClipLoader } from "react-spinners";
import { useForm } from "react-hook-form";

const BeratertageModal = ({ portal, firma, onValueChange }) => {
    const [{ beratertageModalOpen }, dispatch] = useStateValue();
    const [loading, setLoading] = useState(false);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { isValid }
    } = useForm({ mode: "onChange" });

    // ---------- helpers ----------
    const parseGermanNumber = (val) => {
        if (val === null || val === undefined) return 0;
        if (typeof val === "number") {
            // Convert number to German format string, removing unnecessary decimal places
            const num = parseFloat(val.toString());
            if (num % 1 === 0) {
                // Whole number, return without decimal
                return num.toString();
            } else {
                // Has decimal part, format with comma and remove trailing zeros
                return num.toString().replace(".", ",").replace(/,?0+$/, "");
            }
        }

        // Handle string values
        let s = String(val).trim();

        // If it's a decimal number starting with dot (like .700), convert to German format
        if (s.startsWith('.')) {
            const num = parseFloat(s);
            if (num % 1 === 0) {
                return num.toString();
            } else {
                return num.toString().replace(".", ",").replace(/,?0+$/, "");
            }
        }

        // If it already has comma as decimal separator, clean up trailing zeros
        if (s.includes(',') && !s.includes('.')) {
            return s.replace(/,?0+$/, "");
        }

        // If it has dot as decimal separator (like 0.7 or 8.000 or 8.100)
        if (s.includes('.') && s.split('.').length === 2) {
            const num = parseFloat(s);
            if (num % 1 === 0) {
                // Whole number like 8.000 -> 8
                return Math.floor(num).toString();
            } else {
                // Decimal number like 8.100 -> 8,1 or 8.150 -> 8,15
                return num.toString().replace(".", ",").replace(/,?0+$/, "");
            }
        }

        // Return as is for whole numbers
        return s;
    };

    // Convert German formatted string back to numeric value
    const germanToNumber = (val) => {
        if (val === null || val === undefined) return 0;
        if (typeof val === "number") return val;

        let s = String(val).trim();
        // Convert German decimal format (comma) to English (dot) for calculations
        s = s.replace(/\./g, "").replace(",", ".");
        const result = parseFloat(s);
        return isNaN(result) ? 0 : result;
    };

    // accepts "70" or "70%" or "0,7" -> returns fraction 0.7
    const parsePercentToFraction = (val) => {
        if (val === null || val === undefined) return 0;
        let s = String(val).trim();
        const hasPct = s.endsWith("%");
        if (hasPct) s = s.slice(0, -1);
        const n = parseGermanNumber(s);
        return n > 1 ? n / 100 : n;
    };

    const fmtInt = (n) => (Number.isFinite(n) ? Math.max(0, Math.ceil(n)) : 0);

    const fmtDe = (n) =>
        new Intl.NumberFormat("de-DE", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Math.max(0, Math.round(n)));

    // ---------- load defaults ----------
    useEffect(() => {
        const fetchBeratertageData = async () => {
            try {
                setLoading(true);
                const res = await Api().get(`/sp_getDataConsultantCalc/${portal}/${firma}`);
                const entry = res?.data?.[0];
                if (entry) {
                    setValue("MA", parseInt(entry.MA ?? 0));
                    const bq = parseFloat(entry.BeteiligungsQuote ?? 0);
                    setValue("BetQuote", bq > 1 ? bq : bq * 100); // show as 70
                    setValue("BeratungProMA", parseGermanNumber(entry.BeratungenProMA));
                    setValue("BeratungenProTag", parseGermanNumber(entry.BeratungenProTag));
                    setValue("EingesetztBerater", parseGermanNumber(entry.AnzahlBerater));
                    setValue("AnzahlBeratungstage", parseGermanNumber(entry.AnzahlBeratungstage));
                }
            } catch (error) {
                console.error(error);
                toast.error("Fehler beim Laden der Beratertage-Daten.");
            } finally {
                setLoading(false);
            }
        };

        if (beratertageModalOpen) fetchBeratertageData();
    }, [beratertageModalOpen, portal, firma, setValue]);

    // ---------- watched inputs ----------
    const MA = germanToNumber(watch("MA"));
    const BetQuoteFraction = parsePercentToFraction(watch("BetQuote")); // 0..1
    const BeratungProMA = germanToNumber(watch("BeratungProMA")); // e.g. 2
    const BeratungenProTag = Math.max(1, germanToNumber(watch("BeratungenProTag"))); // avoid /0
    const EingesetztBerater = Math.max(1, germanToNumber(watch("EingesetztBerater"))); // avoid /0

    // ---------- derived values (live) ----------
    const { maLtQuote, erfBeratTage } = useMemo(() => {
        const _maLtQuote = fmtInt(MA * BetQuoteFraction); // ceil
        const _erfBeratTage = fmtInt(_maLtQuote * BeratungProMA / BeratungenProTag / EingesetztBerater); // ceil
        return {
            maLtQuote: _maLtQuote,
            erfBeratTage: _erfBeratTage
        };
    }, [MA, BetQuoteFraction, BeratungProMA, BeratungenProTag, EingesetztBerater]);

    // ---------- notify parent of value changes ----------
    useEffect(() => {
        if (onValueChange && erfBeratTage > 0) {
            onValueChange(erfBeratTage);
        }
    }, [erfBeratTage, onValueChange]);

    // ---------- update AnzahlBeratungstage when erfBeratTage changes ----------
    useEffect(() => {
        setValue("AnzahlBeratungstage", erfBeratTage);
    }, [erfBeratTage, setValue]);

    // ---------- save ----------
    const handleSave = async () => {
        try {
            setLoading(true);
            await Api().post("/sp_putConsultantCalc", {
                portal,
                fpId: firma,
                ma: MA,
                betQuote: BetQuoteFraction,
                eingesetzteBerater: EingesetztBerater,
                beratungenProTag: BeratungenProTag,
                beratungenProMA: BeratungProMA,
                anzahlBeratungstage: erfBeratTage
            });

            toast.success("Beratertage erfolgreich gespeichert");
            dispatch({ type: "SET_BERATERTAGE_MODAL_OPEN", item: false });
        } catch (error) {
            console.error(error);
            toast.error("Fehler beim Speichern der Beratertage");
        } finally {
            setLoading(false);
        }
    };

    if (!beratertageModalOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-text bg-opacity-50">
            <div className="relative w-full max-w-3xl bg-white p-6 rounded-lg shadow-lg mx-4 max-h-[90vh] overflow-y-auto overflow-x-hidden">
                <GrClose
                    className="cursor-pointer absolute top-4 right-4"
                    size="22px"
                    onClick={() => dispatch({ type: "SET_BERATERTAGE_MODAL_OPEN", item: false })}
                />

                <h2 className="text-xl font-bold mb-4">Beratertage Planung - Erforderl. Berater-Tage {erfBeratTage}</h2>

                {loading ? (
                    <div className="flex justify-center items-center h-20">
                        <ClipLoader size={32} color={"#b4b4b4"} />
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Row 1 */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="flex items-center gap-2">
                                <label className="text-sm w-40 flex-shrink-0">Mitarbeiter gesamt</label>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <input
                                        {...register("MA")}
                                        type="text"
                                        className="w-20 h-9 p-2 border rounded bg-white text-right"
                                        placeholder="100"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-sm w-40 flex-shrink-0">Beratung pro MA</label>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <input
                                        {...register("BeratungProMA")}
                                        type="text"
                                        className="w-20 h-9 p-2 border rounded bg-white text-right"
                                        placeholder="2"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Row 2 */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="flex items-center gap-2">
                                <label className="text-sm w-40 flex-shrink-0">Beteiligungs-Quote</label>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <input
                                        {...register("BetQuote")}
                                        type="text"
                                        className="w-20 h-9 p-2 border rounded bg-white text-right"
                                        placeholder="70"
                                    />
                                    <span className="text-sm whitespace-nowrap w-16 text-left">%</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-sm w-40 flex-shrink-0">Beratungen pro Tag</label>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <input
                                        {...register("BeratungenProTag")}
                                        type="text"
                                        className="w-20 h-9 p-2 border rounded bg-white text-right"
                                        placeholder="8"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Row 3 */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div className="flex items-center gap-2">
                                <label className="text-sm w-40 flex-shrink-0">Mitarbeiter lt. Quote</label>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <div className="w-20 h-9 p-3 bg-gray-50 text-right flex items-center justify-end">
                                        {fmtDe(maLtQuote)}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-sm w-40 flex-shrink-0">Eingesetzte Berater</label>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <input
                                        {...register("EingesetztBerater")}
                                        type="text"
                                        className="w-20 h-9 p-2 border rounded bg-white text-right"
                                        placeholder="3"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* actions */}
                        <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-4">
                            <button
                                type="button"
                                onClick={() => dispatch({ type: "SET_BERATERTAGE_MODAL_OPEN", item: false })}
                                className="flex-1 py-2 px-4 rounded text-white bg-cancel hover:bg-cancel order-2 sm:order-1"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit(handleSave)}
                                disabled={loading}
                                className={`flex-1 py-2 px-4 rounded text-white order-1 sm:order-2 ${
                                    loading
                                        ? "bg-mainBlue opacity-50 cursor-not-allowed"
                                        : "bg-mainBlue hover:bg-blue-600"
                                }`}
                            >
                                {loading ? "Speichere..." : "Beratertage speichern"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BeratertageModal;
